import React, { useState, useEffect } from "react";
import {
  Filter,
  ShieldAlert,
  Binoculars,
  ClipboardCheck,
  BookOpenCheck,
  Gavel,
  ShieldCheck,
  Rocket,
  Search,
  Plus,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import DemandPageHeader from "./DemandPageHeader";
import { listDemands, getDemandStats } from "../api/demandsClient";
import { stageUiId } from "../constants/demandStages";

// Demand Management Dashboard Component
const DemandDashboard = () => {
  const [demands, setDemands] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStage, setFilterStage] = useState("all");

  // Fetch real data from API
  useEffect(() => {
    const fetchDemands = async () => {
      try {
        setLoading(true);
        setError('');

        // Both calls in parallel — they're independent, and the dashboard was
        // waiting on them serially.
        const [demandsData, statsData] = await Promise.all([
          listDemands({ limit: 100 }),
          getDemandStats(),
        ]);

        // Transform demands data to match UI format
        const transformedDemands = (demandsData.demands || []).map(d => ({
          id: d.demand_id,
          title: d.title,
          description: d.description,
          stage: mapStageToUI(d.current_stage),
          priority: d.urgency?.toLowerCase() || 'medium',
          submittedBy: d.business_owner || 'Unknown',
          submittedDate: d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : 'N/A',
          estimatedCost: 'TBD',
          status: mapStatusToUI(d.stage_status),
          triageScore: d.triage_score,
          triagePass: d.triage_pass,
          recommendedPath: d.recommended_path,
          securityRisk: d.security_risk_rating,
          tabDecision: d.tab_decision,
        }));

        setDemands(transformedDemands);
        setStats(statsData);
        setLoading(false);
      } catch (e) {
        // An empty grid and a logged error looked identical to "no demands yet".
        setError(e.message || String(e));
        setDemands([]);
        setLoading(false);
      }
    };

    fetchDemands();
  }, []);

  // Map backend stage names to UI stage IDs.
  //
  // This was an inline map missing DISCOVERY_COMPLETE, TRIAGE_RETURNED, REJECTED and
  // COMPLETED, ending in `|| 'triage'` — so a demand that had just finished Discovery
  // reappeared in the Triage column with no indication anything was wrong. The
  // canonical map now lives beside the backend's own sequence.
  const mapStageToUI = (backendStage) => stageUiId(backendStage) || 'triage';

  // Map backend status to UI status
  const mapStatusToUI = (backendStatus) => {
    const statusMap = {
      'PENDING': 'pending',
      'IN_PROGRESS': 'in-progress',
      'PASSED': 'in-progress',
      'FAILED': 'pending',
      'BLOCKED': 'pending',
    };
    return statusMap[backendStatus] || 'pending';
  };

  const stages = [
    { id: "triage", label: "Triage", icon: ShieldAlert, color: "var(--cares-amber)" },
    { id: "discovery", label: "Discovery", icon: Binoculars, color: "var(--cares-blue)" },
    { id: "poc", label: "Enhanced Discovery (POC)", icon: ClipboardCheck, color: "var(--cares-purple)" },
    { id: "requirements", label: "AI Requirements", icon: BookOpenCheck, color: "var(--cares-teal)" },
    { id: "security", label: "Security Pre-Screen", icon: ShieldCheck, color: "var(--cares-orange)" },
    { id: "tab", label: "TAB Decision", icon: Gavel, color: "var(--cares-magenta)" },
    { id: "execution", label: "Execution Handoff", icon: Rocket, color: "var(--cares-green)" },
  ];

  const statsCards = stats ? [
    { label: "Total Demands", value: stats.total_demands || 0, icon: Filter, color: "var(--cares-blue)" },
    { label: "Recent (7 days)", value: stats.recent_7_days || 0, icon: TrendingUp, color: "var(--cares-green)" },
    { label: "In Progress", value: demands.filter(d => d.status === "in-progress").length, icon: Clock, color: "var(--cares-amber)" },
    { label: "Pending", value: demands.filter(d => d.status === "pending").length, icon: AlertCircle, color: "var(--cares-orange)" },
  ] : [
    { label: "Total Demands", value: demands.length, icon: Filter, color: "var(--cares-blue)" },
    { label: "Recent (7 days)", value: 0, icon: TrendingUp, color: "var(--cares-green)" },
    { label: "In Progress", value: demands.filter(d => d.status === "in-progress").length, icon: Clock, color: "var(--cares-amber)" },
    { label: "Pending", value: demands.filter(d => d.status === "pending").length, icon: AlertCircle, color: "var(--cares-orange)" },
  ];

  const filteredDemands = demands.filter((demand) => {
    const matchesSearch =
      demand.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      demand.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      demand.submittedBy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = filterStage === "all" || demand.stage === filterStage;
    return matchesSearch && matchesStage;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#ef4444";
      case "medium":
        return "#f59e0b";
      case "low":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  const getStageInfo = (stageId) => {
    return stages.find((s) => s.id === stageId) || stages[0];
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid var(--cares-line-card)',
          borderTop: '4px solid var(--cares-blue)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
      </div>
    );
  }

  return (
    <div style={{
      flex: 1,
      padding: '24px',
      background: 'linear-gradient(135deg, var(--cares-surface-board) 0%, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 50%, color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card)) 100%)',
      minHeight: '100vh'
    }}>
      {error && (
        <div style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: '8px',
          border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))', background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))', color: 'var(--cares-orange)', fontSize: '14px'
        }}>
          Could not load demands: {error}
        </div>
      )}

      {/* Breadcrumb and Header */}
      <DemandPageHeader
        title="Demand Management Dashboard"
        description="Track and manage demands through the 7-stage screening workflow"
        breadcrumbPath="Dashboard"
      />

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {statsCards.map((stat, index) => (
          <div
            key={index}
            style={{
              background: 'var(--cares-surface-card)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              border: '1px solid var(--cares-line-card)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div>
              <p style={{
                fontSize: '14px',
                color: 'var(--cares-text-label)',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                {stat.label}
              </p>
              <p style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: 'var(--cares-text-1)'
              }}>
                {stat.value}
              </p>
            </div>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: `${stat.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <stat.icon style={{ width: '28px', height: '28px', color: stat.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* Pipeline Stages */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line-card)',
        marginBottom: '32px'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: 'var(--cares-text-1)',
          marginBottom: '20px'
        }}>
          Pipeline Stages
        </h2>
        <div style={{
          display: 'flex',
          gap: '12px',
          overflowX: 'auto',
          paddingBottom: '8px',
          paddingTop: '8px'
        }}>
          {stages.map((stage, index) => (
            <div
              key={stage.id}
              style={{
                flex: '1',
                minWidth: '140px',
                background: `${stage.color}10`,
                border: `2px solid ${stage.color}`,
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '8px'
              }}>
                <stage.icon style={{ width: '24px', height: '24px', color: stage.color }} />
              </div>
              <p style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--cares-text-1)'
              }}>
                {stage.label}
              </p>
              <p style={{
                fontSize: '12px',
                color: 'var(--cares-text-label)',
                marginTop: '4px'
              }}>
                {demands.filter(d => d.stage === stage.id).length} demands
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Search and Filter */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line-card)'
      }}>
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '20px',
              color: 'var(--cares-text-note)'
            }} />
            <input
              type="text"
              placeholder="Search demands by title, ID, or submitter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                border: '2px solid var(--cares-line-card)',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--cares-blue)';
                e.target.style.boxShadow = '0 0 0 3px rgba(66, 153, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--cares-line-card)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>


          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '2px solid var(--cares-line-card)',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              background: 'var(--cares-surface-card)',
              minWidth: '200px'
            }}
          >
            <option value="all">All Stages</option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.label}
              </option>
            ))}
          </select>

          <button
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-purple) 100%)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 12px rgba(66, 153, 225, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <Plus style={{ width: '18px', height: '18px' }} />
            New Demand
          </button>
        </div>

        {/* Demands List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredDemands.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px',
              color: 'var(--cares-text-label)'
            }}>
              <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                No demands found
              </p>
              <p style={{ fontSize: '14px' }}>
                Try adjusting your search or filter criteria
              </p>
            </div>
          ) : (
            filteredDemands.map((demand) => {
              const stageInfo = getStageInfo(demand.stage);
              return (
                <div
                  key={demand.id}
                  style={{
                    background: 'var(--cares-surface-board)',
                    border: '1px solid var(--cares-line-card)',
                    borderRadius: '12px',
                    padding: '20px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = 'var(--cares-line-strong)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'var(--cares-line-card)';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: 'var(--cares-text-1)'
                        }}>
                          {demand.title}
                        </h3>
                        <span style={{
                          padding: '4px 12px',
                          background: `${getPriorityColor(demand.priority)}20`,
                          color: getPriorityColor(demand.priority),
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {demand.priority}
                        </span>
                      </div>
                      <p style={{
                        color: 'var(--cares-text-label)',
                        fontSize: '14px',
                        marginBottom: '12px'
                      }}>
                        {demand.description}
                      </p>
                      <div style={{
                        display: 'flex',
                        gap: '24px',
                        fontSize: '13px',
                        color: 'var(--cares-text-label)'
                      }}>
                        <span><strong>ID:</strong> {demand.id}</span>
                        <span><strong>Submitted by:</strong> {demand.submittedBy}</span>
                        <span><strong>Date:</strong> {demand.submittedDate}</span>
                        <span><strong>Est. Cost:</strong> {demand.estimatedCost}</span>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      background: `${stageInfo.color}20`,
                      border: `2px solid ${stageInfo.color}`,
                      borderRadius: '8px'
                    }}>
                      <stageInfo.icon style={{
                        width: '18px',
                        height: '18px',
                        color: stageInfo.color
                      }} />
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--cares-text-1)'
                      }}>
                        {stageInfo.label}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DemandDashboard;
